export interface ProcessStep {
  num: string;
  icon: string;
  title: string;
  desc: string;
}

interface Props {
  title?: string;
  subtitle?: string;
  steps: ProcessStep[];
}

export default function SectionProcess({ title = 'How We Defend Your Case', subtitle, steps }: Props) {
  return (
    <section className="bg-white py-24 relative overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #c9a84c 0, #c9a84c 1px, transparent 0, transparent 80px), repeating-linear-gradient(90deg, #c9a84c 0, #c9a84c 1px, transparent 0, transparent 80px)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-[11px] font-bold uppercase tracking-[0.28em]">Our Process</span>
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
          </div>
          <h2
            className="font-bold text-[#0c1520] leading-tight mb-5"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4vw, 52px)' }}
          >
            {title}
          </h2>
          <div className="flex items-center justify-center gap-2.5 mb-5">
            <div className="w-10 h-[3px] bg-[#c9a84c] rounded-full" />
            <div className="w-3 h-[3px] bg-[#c9a84c]/35 rounded-full" />
          </div>
          {subtitle && <p className="text-[#64748b] text-lg max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 relative">
          {/* Connecting line behind cards */}
          <div className="absolute top-[56px] left-[12.5%] right-[12.5%] h-[1px] hidden lg:block" style={{ background: 'repeating-linear-gradient(to right, #c9a84c 0, #c9a84c 6px, transparent 6px, transparent 16px)' }} />

          {steps.map((step, idx) => (
            <div key={step.num} className="relative px-3 first:pl-0 last:pr-0">
              <div className="group relative bg-[#f9f8f4] border border-[#ece9de] rounded-2xl p-7 h-full hover:border-[#c9a84c]/50 hover:-translate-y-2 hover:shadow-xl hover:shadow-[#c9a84c]/10 transition-all duration-400 overflow-hidden">
                {/* Gold hover overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#c9a84c] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Step icon + big number */}
                <div className="flex items-start justify-between mb-7" style={{ zIndex: 1 }}>
                  <div className="w-[50px] h-[50px] flex items-center justify-center bg-[#050a12] rounded-xl group-hover:bg-[#c9a84c] transition-colors duration-300 flex-shrink-0">
                    <i className={`${step.icon} text-[#c9a84c] text-xl group-hover:text-[#050a12] transition-colors duration-300`} />
                  </div>
                  <span
                    className="text-[72px] font-black text-[#c9a84c]/10 leading-none select-none group-hover:text-[#c9a84c]/18 transition-colors duration-300"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {step.num}
                  </span>
                </div>

                <h3 className="text-[#0c1520] font-bold text-xl mb-3 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {step.title}
                </h3>
                <p className="text-[#64748b] text-sm leading-relaxed">{step.desc}</p>

                {/* Desktop step connector arrow */}
                {idx < steps.length - 1 && (
                  <div className="absolute -right-2 top-[56px] -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#c9a84c]/60 hidden lg:flex" style={{ zIndex: 2 }}>
                    <i className="ri-arrow-right-s-line text-lg" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
